import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import adminService from '@/services/adminService.ts';
import type { ManagerSummary } from '@/types/admin.ts';

interface ChangeCompanyModalProps {
  target: ManagerSummary | null;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}

export default function ChangeCompanyModal({ target, onClose, onSaved }: ChangeCompanyModalProps) {
  const { t } = useTranslation();
  const [company, setCompany] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isOpen = target !== null;

  useEffect(() => {
    if (target) {
      setCompany(target.company);
      setError(null);
      setSubmitting(false);
    }
  }, [target]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!target) return;
    setError(null);

    const trimmed = company.trim();
    if (!trimmed) {
      setError(t('superuser.admin.users.createModal.companyRequired', {
        defaultValue: 'Empresa es obligatoria.',
      }));
      return;
    }

    setSubmitting(true);
    try {
      await adminService.changeUserCompany(target.id, { company: trimmed });
      await onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t('superuser.admin.users.changeCompanyModal.title', { defaultValue: 'Cambiar empresa' })}
          </DialogTitle>
          <DialogDescription>
            {target && t('superuser.admin.users.changeCompanyModal.description', {
              name: `${target.firstName} ${target.lastName}`,
              count: target.salespersonCount,
              defaultValue:
                'Cambiar la empresa de {{name}} también moverá a los {{count}} vendedores que tenga asignados al nuevo tenant.',
            })}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground">
              {t('superuser.admin.users.companyLabel', { defaultValue: 'Empresa' })}
              <span className="ml-0.5 text-destructive">*</span>
            </label>
            <input
              type="text"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              required
              autoFocus
              className="w-full rounded-lg border border-border bg-card px-3 py-2 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
          </div>

          {error && (
            <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={submitting}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting && <Loader2 className="size-4 animate-spin" />}
              {t('common.saveChanges')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
